<template>
	<div :class="['profile-view', { 'profile-view--b2c': isB2cProfile }]">
		<profile-layout v-if="isB2cProfile" title="Личный кабинет" :email="email">
			<base-card class="profile-card">
				<plan-card-pro v-if="isPro" />
				<plan-card-free v-else />
			</base-card>
		</profile-layout>
		<template v-else>
			<header-component type="profile" />
			<div class="profile-number-wrap">
				<div class="profile-number-icon-wrap">
					<svg-icon name="profile" :size="20" class="profile-number-icon" />
				</div>
				<span v-if="msisdnFormat" class="profile-number">{{ msisdnFormat }}</span>
			</div>
		</template>
		<div class="profile-menu-list">
			<profile-menu-item text="Условия обработки персональных данных" link="/privacy-policy" />
			<profile-menu-item text="Пользовательское соглашение" link="/eula" />
			<!-- Видимость ссылки повторяет доступность самого /cookie-policy: маршрут открыт для
				tcell, BY-редакций и b2c (p3) — см. гейт в router/routes.ts. -->
			<profile-menu-item
				text="Политика обработки cookie-файлов"
				link="/cookie-policy"
				v-if="isByLocal || isTcell || isB2cProfile"
			/>
			<profile-menu-item text="Настройки Cookie" type="btn" @click="openCookiesSetting" />
			<profile-menu-item text="Правила предоставления услуги" link="/terms-of-service" v-if="isTcell" />
		</div>
		<div v-if="isB2cProfile" class="profile-logout">
			<base-button variant="secondary" text="Выйти" icon="logout" @click="openIsLogoutDialogOpened" />
		</div>
		<Teleport v-else to="body">
			<div class="profile-btn-wrap">
				<base-button
					variant="secondary"
					text="Выйти"
					:icon="isMts ? undefined : 'logout'"
					@click="openIsLogoutDialogOpened"
				/>
			</div>
		</Teleport>
	</div>
	<dialog-component
		title="Вы уверены что хотите выйти из аккаунта?"
		custom-class="dialog-logout"
		v-if="isLogoutDialogOpened"
		@close="closeIsLogoutDialogOpened"
		@clickSecondBtn="closeIsLogoutDialogOpened"
		@clickFirstBtn="logoutClickHandler"
	/>
</template>

<script setup lang="ts">
import ProfileMenuItem from '@/components/ProfileMenuItem.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseCard from '@/components/base/BaseCard.vue'
import HeaderComponent from '@/components/HeaderComponent.vue'
import ProfileLayout from '@/components/profile/ProfileLayout.vue'
import PlanCardFree from '@/components/profile/PlanCardFree.vue'
import PlanCardPro from '@/components/profile/PlanCardPro.vue'
import DialogComponent from '@/components/dialogs/DialogComponent.vue'
import SvgIcon from '@/components/SvgIcon.vue'
import { ref, onMounted, computed } from 'vue'
import AppConfig from '@/config'
import { useAuthStore } from '@/stores/auth'
import { useProfileStore } from '@/stores/profile'
import { useCookiesStore } from '@/stores/cookies'
import { useUsageStore } from '@/stores/usage'
import { isTcell, isByLocal, isMts } from '@/config/tenant'

const authStore = useAuthStore()
const profileStore = useProfileStore()
const cookiesStore = useCookiesStore()
const usageStore = useUsageStore()

// Единственное условие нового кабинета. В остальных сборках авторизация внешняя,
const isB2cProfile = AppConfig.internalB2C

const isLogoutDialogOpened = ref(false)
const msisdnFormat = computed(() => profileStore.msisdnFormat)
// В b2c пользователь известен по e-mail: msisdn у него может не быть вовсе.
const email = computed(() => profileStore.email)
// Тот же приоритет, что в useFeatureGate: /usage авторитетен, /user — быстрый дубль.
const isPro = computed(() => usageStore.isPro || profileStore.plan === 'pro')

async function logoutClickHandler() {
	authStore.logout()
}

function closeIsLogoutDialogOpened() {
	isLogoutDialogOpened.value = false
}

function openIsLogoutDialogOpened() {
	isLogoutDialogOpened.value = true
}

onMounted(async () => {
	await profileStore.fetchProfile()
})

function openCookiesSetting() {
	cookiesStore.openCookiesSettings()
}
</script>

<style lang="scss" scoped>
.profile {
	&-view {
		@include flex-column-start;
		background-color: var(--background-primary);
		overflow: hidden;
		position: relative;
	}
	&-btn-wrap {
		position: absolute;
		bottom: 0;
		width: 100%;
		padding: 24px;
		@include flex-center;
		& .btn {
			max-width: 500px;
		}
		@if $is-mts {
			:deep(.btn-secondary) {
				background: var(--background-secondary);
				padding: 16px;
				&:hover,
				&:active {
					background: var(--background-secondary);
				}
			}
			:deep(.btn-text) {
				color: var(--text-primary);
				text-align: center;
			}
		}
	}
	&-view--b2c {
		overflow: visible;
		.profile-menu-list {
			gap: 12px;
			margin-bottom: 24px;
			> * {
				margin-bottom: 0;
			}
		}
	}
	&-card {
		margin-bottom: 28px;
	}
	&-logout {
		width: 100%;
		padding-bottom: 24px;
		@include flex-center;
		& .btn {
			max-width: 500px;
		}
	}
	&-menu-list {
		width: 100%;
		margin-bottom: 40px;
		@include flex-column-center;
		> * {
			margin-bottom: 8px;
		}
	}
	&-number {
		color: var(--text-primary);
		font: var(--font-subtitle);
		&-wrap {
			@include flex-center;
			margin-bottom: 12px;
		}
		&-icon-wrap {
			width: 32px;
			height: 32px;
			color: var(--text-brand);
			background-color: var(--background-brand-alpha);
			border-radius: var(--radius-sm);
			@include flex-center;
			margin-right: 8px;
			@if $is-mts {
				color: var(--text-secondary);
				background-color: var(--background-secondary);
			}
		}
	}
}
.fixed-top {
	position: fixed;
	top: -1px;
	left: 0;
	right: 0;
	width: 100%;
	margin: 0 auto;
	background: var(--background-primary);
	z-index: 200;
}

.header {
	@include flex-space-between;
	width: 100%;
	padding: $padding-container;
	padding-top: 8px;
	padding-bottom: 24px;
	max-width: $max-content-width;
	margin: 0 auto;
}
</style>
